// Pure view-model helpers for the grouped mission UI (captain's Mission tab, base mission board) and the render-decision
// rule that keeps the captain's tabs from going blank when the room is re-entered. No DOM here, so they are unit-tested
// headlessly (scripts/mission-groups-test.ts).
import type { MissionObjective, MissionRewardItem } from '../../shared/types.js';
import type { MissionVisualState } from './missionDetail.js';

export type MissionGroupView = {
  groupId: string;
  /** The overarching mission's title (a legacy level without a groups table falls back to its first sub-mission's title). */
  title: string;
  summary?: string;
  reward: MissionRewardItem[];
  /** True when the level declares the group (has a title); false for a lone legacy mission or an undeclared prefix group. */
  declared: boolean;
  /** Indices into the objective list: every member, then only the main (non-optional) ones. */
  indices: number[];
  mainIndices: number[];
  state: MissionVisualState;
  /** Main sub-missions completed / in total. */
  done: number;
  total: number;
  /** Index (into the objective list) of the active sub-mission, or -1. */
  activeIndex: number;
  /** Zero-based position among the level's main groups (absent for optional-only groups). */
  groupIndex?: number;
  groupCount?: number;
};

/** Groups the objective list by `groupId`, in first-appearance order, with the progress the UI needs. */
export const buildGroupViews = (missions: readonly MissionObjective[], states: readonly MissionVisualState[]): MissionGroupView[] => {
  const byId = new Map<string, number[]>();
  missions.forEach((m, i) => {
    const list = byId.get(m.groupId);
    if (list) list.push(i); else byId.set(m.groupId, [i]);
  });
  const views: MissionGroupView[] = [];
  for (const [groupId, indices] of byId) {
    const mainIndices = indices.filter((i) => !missions[i].optional);
    const first = missions[indices[0]];
    const lead = missions[mainIndices[0] ?? indices[0]];
    const done = mainIndices.filter((i) => states[i] === 'done').length;
    const activeIndex = mainIndices.find((i) => states[i] === 'active') ?? -1;
    const state: MissionVisualState = mainIndices.length === 0
      ? (indices.every((i) => states[i] === 'done') ? 'done' : 'pending')
      : done === mainIndices.length ? 'done' : activeIndex >= 0 ? 'active' : 'pending';
    views.push({
      groupId,
      title: lead.groupTitle ?? first.title,
      summary: lead.groupSummary,
      reward: lead.groupReward ?? [],
      declared: typeof lead.groupTitle === 'string',
      indices,
      mainIndices,
      state,
      done,
      total: mainIndices.length,
      activeIndex,
      groupIndex: lead.groupIndex,
      groupCount: lead.groupCount
    });
  }
  return views;
};

/** Is this group shown as a collapsible overarching row with a checklist (true), or as one plain mission row (false)? */
export const isGroupRow = (view: MissionGroupView): boolean => view.declared || view.indices.length > 1;

/** "Step 2 of 4", "Complete" or "4 steps": the progress chip on an overarching mission. */
export const groupProgressLabel = (view: MissionGroupView): string => {
  if (view.total === 0) return '';
  if (view.state === 'done') return 'Complete';
  if (view.state === 'active') return `Step ${Math.min(view.total, view.done + 1)} of ${view.total}`;
  return `${view.total} step${view.total === 1 ? '' : 's'}`;
};

/**
 * Should a panel be (re)rendered? The signature gate exists to skip identical snapshots, but it must never skip when the
 * target element is not the one the last render filled: the captain controller rebuilds its tab panels every time the
 * room is entered, and a stale signature then left the fresh, empty panel blank.
 */
export const needsPanelRender = (opts: { force: boolean; signature: string; lastSignature: string; target: unknown; lastTarget: unknown }): boolean =>
  opts.force || opts.signature !== opts.lastSignature || opts.target !== opts.lastTarget;
